import { useEffect, useId, useRef } from "react";
import { CloseIcon, ICON_STROKE } from "shared/icons";

export interface ModalProps {
  title: string;
  /** Optional one-line context under the title. */
  subtitle?: string;
  onCancel: () => void;
  /** Action buttons, rendered right-aligned in the footer. */
  footer: JSX.Element;
  children: React.ReactNode;
}

/**
 * Shared dialog shell for the app's modals.
 *
 * The export, import and seed pickers each carried an identical copy of
 * Tailwind UI's modal boilerplate, and all three copies shared the same bugs:
 * the backdrop was fully opaque (`bg-opacity-75` cannot apply alpha to a
 * `var()` colour, so the page behind vanished), two pointed `aria-labelledby`
 * at an id that did not exist, and `focus:outline-none` suppressed the global
 * focus ring on every button. Fixing it here fixes it everywhere.
 *
 * The body scrolls inside the dialog, so a long list — the seed picker can
 * hold a thousand tracks — keeps the title and actions on screen.
 *
 * Escape and a click on the backdrop both dismiss, via the same `onCancel` as
 * the Cancel button, so they discard exactly what Cancel would.
 */
export function Modal(props: ModalProps): JSX.Element {
  const titleId = useId();

  // Callers pass a fresh arrow each render; reading it through a ref keeps the
  // listener subscribed once rather than re-binding on every render.
  const onCancelRef = useRef(props.onCancel);
  onCancelRef.current = props.onCancel;

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      // `isComposing`: Escape also cancels an IME composition, which shouldn't
      // take the dialog with it.
      if (
        event.key !== "Escape" ||
        event.isComposing ||
        event.defaultPrevented
      ) {
        return;
      }
      onCancelRef.current();
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      {/* The backdrop covers everything outside the panel. A click is
          dispatched to the nearest element containing both press and release,
          so a drag that starts in a field and ends out here lands on the
          wrapper, not the backdrop, and doesn't close the dialog. */}
      <div
        className="modal-backdrop absolute inset-0"
        aria-hidden="true"
        onClick={props.onCancel}
      ></div>

      <div className="relative w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[85vh] flex flex-col bg-surface border border-rule-strong rounded-t-token sm:rounded-token shadow-2xl overflow-hidden">
        <div className="flex-none flex items-start gap-3 px-5 pt-4 pb-3 border-b border-rule">
          <div className="min-w-0 flex-1">
            <h2
              id={titleId}
              className="text-lg font-semibold text-ink leading-tight m-0"
            >
              {props.title}
            </h2>
            {props.subtitle && (
              <p className="text-sm text-ink-2 mt-1 mb-0">{props.subtitle}</p>
            )}
          </div>
          <button
            type="button"
            className="button-small flex-none -mr-1"
            onClick={props.onCancel}
            aria-label="Close"
            title="Close"
          >
            <CloseIcon size={18} strokeWidth={ICON_STROKE} aria-hidden="true" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
          {props.children}
        </div>

        <div className="flex-none flex flex-wrap justify-end gap-2 px-5 py-3 bg-raised border-t border-rule">
          {props.footer}
        </div>
      </div>
    </div>
  );
}

export default Modal;
